/*
 * Search query normalizer. Four independent jobs, in this order:
 *
 * 1. DIMENSIONS. Converts a typed size like "36x36", "36 x 36", "42X42",
 *    "36x36 inches" into an exact tag query ("tag:36x36") so search returns
 *    only that size instead of Shopify's fuzzy token match. Other words are
 *    kept and AND automatically once a tag: clause is present (e.g.
 *    "40x60 brigitte" -> "tag:40x60 brigitte").
 *
 * 2. EXACT TITLES. Roughly 45% of real queries name a specific painting —
 *    "evening glow", "ray of hope", "dockside melody". Shopify ORs the words
 *    and ranks single-word partials above the exact phrase, so the painting
 *    someone actually typed is not even on page 1:
 *
 *        evening glow    -> 65 results; page 1 is Glow, Evening, Island Glow,
 *                           Autumn Glow, Tranquil Evening, Evening Beckons.
 *                           "Evening Glow" itself is buried.
 *        "evening glow"  -> 1, the painting.
 *
 *    ⚠️ Quoting CANNOT be applied blindly — half of real multi-word queries
 *    match nothing as a phrase, and a zero-result page is a bounce:
 *
 *        "ray of hope"           1     "harrison muskoka"        0
 *        "water lilies"          1     "brigitte gradton"        0
 *        "georgian bay"        461     "long painting"           0
 *                                      "paintings of westport"   0
 *
 *    So the phrase is ASKED ABOUT before it is committed to. /search/suggest.json
 *    honours quoted phrases and agrees exactly with full search (verified
 *    2026-08-27), so one small request on submit decides it with no extra page
 *    load and no flash of a wrong result set.
 *
 * 3. THE LIVE DROPDOWN. Wokiee owns the type-ahead panel under the search box;
 *    this file takes that handler over so the dropdown can be held to the same
 *    standard as the results page. See the block comment above
 *    takeOverDropdown() for why the takeover is safe and what it changes.
 *
 * 4. THE AVAILABLE COUNT. 71% of the catalogue is sold, so the number worth
 *    showing is how many paintings can actually be bought — `sky` matches 991
 *    and 269 are for sale. One request per search page answers that, and the
 *    same answer decides whether the price-sort links keep their availability
 *    clause. See the block comment above probeAvailable().
 *
 * All four paths fail open: anything unexpected submits or searches the query
 * as typed, or leaves the server-rendered page exactly as it was — the
 * behaviour that shipped before this file existed.
 */
(function () {
  var DIMENSION = /(\d+)\s*[x×]\s*(\d+)(?:\s*(?:inches|inch|in)\b|\s*["”])?/gi;

  /* Any longer and a slow connection stalls the search box; the fallback on
     timeout is the loose query, so erring short costs nothing. */
  var PROBE_TIMEOUT_MS = 600;

  /* A field clause means the query is already precise — quoting it would fight
     the clause for the same string. Covers the tag: this file writes and the
     vendor: the Artist facet writes. */
  var FIELD_CLAUSE = /\b(?:tag|vendor|product_type|title|sku|barcode):/i;
  var ANY_QUOTE = /["“”]/;

  /* Appended to a query to narrow it to work that is actually for sale. Used by
     both the results heading and the dropdown's "see all" count. */
  var AVAIL_CLAUSE = ' AND tag:available';

  function normalizeArtSearch(q) {
    if (!q) return q;
    if (/\btag:/i.test(q)) return q; // already an advanced/processed query — leave alone
    return q.replace(DIMENSION, 'tag:$1x$2');
  }

  function tidy(q) {
    return String(q).replace(/\s+/g, ' ').trim();
  }

  /* Only multi-word, unquoted, clause-free queries are worth a phrase probe.
     A single word is already its own phrase, so quoting it changes nothing. */
  function isPhraseCandidate(q) {
    var t = tidy(q);
    if (!t) return false;
    if (ANY_QUOTE.test(t)) return false;
    if (FIELD_CLAUSE.test(t)) return false;
    return t.indexOf(' ') !== -1;
  }

  /* Resolves true when the quoted phrase matches at least one product.
     Never rejects — every failure path resolves false, meaning "submit as
     typed". limit=1 because only the existence of a match matters. */
  function phraseHasMatch(phrase) {
    return new Promise(function (resolve) {
      var settled = false;
      function done(hit) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(hit);
      }
      var timer = setTimeout(function () { done(false); }, PROBE_TIMEOUT_MS);

      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent('"' + phrase + '"')
        + '&resources[type]=product&resources[limit]=1';

      try {
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (json) {
            var r = json && json.resources && json.resources.results;
            var products = r && r.products;
            done(!!(products && products.length));
          })
          .catch(function () { done(false); });
      } catch (err) {
        done(false);
      }
    });
  }

  /* Navigate with a rewritten query WITHOUT altering the visible input, so
     shoppers never see the tag: or quoted form flash in the search box. */
  function goWith(form, q) {
    var action = form.getAttribute('action') || '/search';
    var params = new URLSearchParams();
    new FormData(form).forEach(function (val, key) {
      if (key !== 'q') params.append(key, val);
    });
    params.set('q', q);
    window.location.assign(action + (action.indexOf('?') === -1 ? '?' : '&') + params.toString());
  }

  // 1) Rewrite the query on submit for every search form on the page.
  function bindForms() {
    var inputs = document.querySelectorAll('form input[name="q"]');
    for (var i = 0; i < inputs.length; i++) {
      var form = inputs[i].form;
      if (!form || form.dataset.artNormBound) continue;
      form.dataset.artNormBound = '1';
      form.addEventListener('submit', function (e) {
        var form = this;
        var input = form.querySelector('input[name="q"]');
        if (!input) return;

        /* A probe is already in flight from a double Enter. Swallow the repeat
           rather than racing two navigations. */
        if (form.dataset.artNormBusy) { e.preventDefault(); return; }

        var typed = input.value;

        /* Dimensions first, and they win outright: once a tag: clause exists
           the query is precise, and isPhraseCandidate would reject it anyway. */
        var normalized = normalizeArtSearch(typed);
        if (normalized !== typed) {
          e.preventDefault();
          goWith(form, normalized);
          return;
        }

        /* No phrase probe possible or warranted — submit as typed, unchanged
           from the behaviour before this file handled phrases. */
        if (!window.fetch || !window.Promise || !isPhraseCandidate(typed)) return;

        e.preventDefault();
        form.dataset.artNormBusy = '1';
        var phrase = tidy(typed);
        phraseHasMatch(phrase).then(function (hit) {
          delete form.dataset.artNormBusy;
          goWith(form, hit ? '"' + phrase + '"' : typed);
        });
      });
    }
  }

  // 2) Safety net: normalize direct hits to /search (bookmarks, external links).
  //    ⚠️ Do NOT add a filter.* param here. A valid filter param makes Shopify
  //    discard the tag: clause this function just built, and the search falls
  //    back to fuzzy text — measured 2026-08-27: tag:36x36 187 -> 572.
  //
  //    Phrases are deliberately NOT probed here. This runs after the page has
  //    already rendered, so quoting would mean loading the loose results and
  //    then throwing them away — a visible double load on exactly the queries
  //    that are most common. The form path above catches these before any
  //    navigation, and a bookmarked loose query still works, just loosely.
  function redirectIfNeeded() {
    if (window.location.pathname.indexOf('/search') === -1) return;
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (!q) return;
    var changed = false;

    /* Every search form in the theme posts type=product and goWith() carries it
       through, but a bookmark, an old link or a search-engine hit can arrive
       without it — and Shopify then searches pages and articles as well. Those
       render through the product card, which reads a page's missing `available`
       as SOLD, so /search?q=gra came back as two "Sold" cards pointing at
       /pages/artists and /pages/for-the-art-obsessed. Filled in only when the
       param is absent, so an explicit type= is still honoured. */
    if (!params.get('type')) {
      params.set('type', 'product');
      changed = true;
    }

    var normalized = normalizeArtSearch(q);
    if (normalized !== q) {
      params.set('q', normalized);
      changed = true;
    }

    // One navigation even when both applied.
    if (changed) {
      window.location.replace(window.location.pathname + '?' + params.toString());
    }
    return changed;
  }

  /* 3) Take the live dropdown over from the vendor.
   *
   * Wokiee binds its own `keyup change` handler to every search input from
   * inside $(document).ready (assets/theme.min.js, around offset 105200) and
   * hardcodes both the URL it fetches and the markup it draws. Everything worth
   * changing about the dropdown — the query it sends, whether it can name an
   * artist, whether it admits a painting is sold — lives inside that closure,
   * so owning the handler is the first move and the only fragile one.
   *
   * Load order is what makes it safe. theme.min.js and this file are both
   * `defer` and theme.min.js is included first (layout/theme.liquid:441 against
   * :500), so deferred execution order registers the vendor's ready callback
   * before ours and jQuery runs ready callbacks in registration order. By the
   * time this one fires the vendor has already appended its .search-results
   * containers and bound its handler, so .off() has something to remove and the
   * containers are there to reuse.
   *
   * It fails loudly rather than quietly: a missed .off() shows two dropdowns, a
   * failed rebind shows none. Both are visible in seconds.
   *
   * The vendor's rendering is reproduced as it stood. What changes is when the
   * dropdown fires and what it asks for:
   *
   *   - A 250ms debounce. The vendor fires one full search render per
   *     keystroke, so typing "brigitte" costs 5 requests (~1.0s of server work
   *     measured 2026-08-28). Debounced it costs 1.
   *   - A 3-character threshold instead of 4, matching the rest of search.
   *   - The typed term goes through the same query decision Enter makes, so
   *     the panel and the results page stop disagreeing. Before this the
   *     dropdown drew "evening glow" from 65 where Enter gave 1, and claimed
   *     511 for "36x36"; both now report what Enter reports (1 and 187,
   *     measured 2026-08-28 — the counts move as the catalogue does, the
   *     agreement is the point).
   *   - The term is URL-encoded. It was interpolated raw, so a "&" or a "#"
   *     in a typed title truncated the query or dropped part of it.
   *   - Matching ARTISTS are listed above the paintings, from the `artists`
   *     array templates/search.json.liquid now returns. This is the only
   *     thing that answers a half-typed name; see the note below.
   *
   * ⚠️ THE 3-CHARACTER THRESHOLD ONLY EARNS ITS KEEP THROUGH THE ARTIST ROW.
   * Shopify's /search matches WHOLE TOKENS, not prefixes, so a partial name
   * returns no PRODUCT at any threshold — measured 2026-08-28: bri 0, brig 1,
   * brigi 0, brigitte 166; har 0, gra 0, gran 0. The vendor's 4-character
   * minimum was no better. Appending a wildcard is NOT the fix: "bri*"
   * returns 318 led by Bring, Bright, Brighter, Brier and not one Brigitte
   * Granton painting, and it silently reorders queries it does not otherwise
   * change ("brigitte*" still 166, "tag:36x36*" still 187, both in a
   * different order). What someone typing "bri" wants is the ARTIST, who is a
   * vendor and appears in no product title — which is why the answer had to
   * come from a Liquid match against the artist collections rather than from
   * anything the query could be rewritten into.
   */
  var DROPDOWN_DEBOUNCE_MS = 250;
  var DROPDOWN_MIN_CHARS = 3;

  /* The vendor draws 6 tiles and then offers "see all" if there are more. Its
     own comment claims the endpoint returns at most 10; either way 6 is what
     gets rendered. */
  var DROPDOWN_MAX_ITEMS = 6;

  function takeOverDropdown($) {
    // Shared, as the vendor shared it: only one search box is in use at a time,
    // so a keystroke in the mobile panel should cancel the desktop box's fetch.
    var inFlight = null;
    /* Guards the available-count reply against a shopper who kept typing. */
    var countToken = 0;

    $('form[action*="/search"]').each(function () {
      var form = $(this);
      var input = form.find('input[name="q"]');
      var resultsList = form.find('.search-results');

      /* No container means the vendor never ran for this form — leave the form
         alone rather than inventing a dropdown it never had. */
      if (!input.length || !resultsList.length) return;

      input.off('keyup change');

      var timer = null;

      /* The artists templates/search.json.liquid matched, drawn above the
         paintings.

         ⚠️ Deliberately NOT list items. theme.css clamps the vendor's <ul> to
         two <li> on desktop and three on mobile, and both selectors reach only
         list items — so an artist rendered outside the list gets its own slot
         and the paintings keep all of theirs. theme.css is generated from
         theme.css.liquid and cannot be edited; the styling is in cg.css.

         Names go in with .text(), not string concatenation, because they are
         the one thing here that comes from shop data rather than this file. */
      function renderArtists(artists, last) {
        var box = $('<div class="cg-dd-artists' + (last ? ' cg-dd-artists--last' : '') + '"></div>');
        box.append($('<span class="cg-dd-artists__label"></span>')
          .text(artists.length === 1 ? 'Artist' : 'Artists'));

        $.each(artists, function (i, artist) {
          var row = $('<a class="cg-dd-artist"></a>').attr('href', artist.url);
          var thumb = $('<span class="cg-dd-artist__thumb"></span>');
          if (artist.image) {
            /* The CDN URL already carries ?v=, so width appends with &, the
               same way snippets/search-artists.liquid builds it. */
            thumb.append($('<img alt="" width="40" height="40" loading="lazy" decoding="async">')
              .attr('src', artist.image + '&width=96'));
          } else {
            /* An artist with no curated crop is still listed — hiding them
               would defeat the point — behind a plain initial tile. */
            thumb.addClass('cg-dd-artist__thumb--empty')
              .attr('aria-hidden', 'true')
              .text(artist.name.charAt(0).toUpperCase());
          }
          row.append(thumb);
          row.append($('<span class="cg-dd-artist__name"></span>').text(artist.name));
          box.append(row);
        });

        return box;
      }

      /* The vendor's markup, unchanged. searchURL is passed in rather than
         rebuilt from the typed term, so "see all" opens the query that
         produced these tiles — it used to link the raw term and so led to a
         different, looser result set than the one it was counting. */
      function render(data, searchURL, resolvedQ) {
        resultsList.empty();

        var artists = data.artists || [];

        /* Zero products is no longer the same as nothing to show. "bri" and
           "gra" match no product text at any length — Shopify matches whole
           tokens, and the artist is a vendor absent from every title — but
           they do name an artist, and that is the answer the shopper wanted.
           Hiding the panel here was what made a half-typed name look broken. */
        if (data.results_count == 0 && artists.length === 0) {
          resultsList.hide();
          return;
        }

        /* `last` drops the rule under the artists when no paintings follow, so
           it never divides the panel from empty space. */
        if (artists.length) resultsList.append(renderArtists(artists, data.results_count == 0));

        if (data.results_count == 0) {
          resultsList.fadeIn(200);
          return;
        }

        var list = resultsList.append('<ul></ul>').find('ul');
        $.each(data.results, function (index, item) {
          if (index >= DROPDOWN_MAX_ITEMS) return false;
          var link = $('<a></a>').attr('href', item.url);
          link.append('<div class="thumbnail"><img src="' + item.thumbnail + '" /></div>');
          var desc = $('<div class="tt-description"><div class="tt-title">' + item.title + '</div></div>');

          /* Sold work stays in the panel and says so. Dropping it emptied the
             panel on most exact-title searches — a sold painting is usually the
             only thing its own title matches — and /search shows sold work, so
             hiding it here would put the dropdown and Enter back into
             disagreement. templates/search.json.liquid decides the state; this
             only draws it.

             Under the title, not over the thumbnail the way the results grid
             draws the same mark: the grid overlays a full-width image, this
             thumbnail is 40px square and a chip would cover most of it. */
          if (item.state) {
            desc.append($('<span class="cg-dd-badge"></span>')
              .addClass('cg-dd-badge--' + item.state)
              .text(item.state_label));
          }

          link.append(desc);
          link.wrap('<li></li>');
          list.append(link.parent());
        });

        if (data.results_count > DROPDOWN_MAX_ITEMS) {
          var seeAll = window.seeallresults || 'See all results';
          /* Empty until the available count lands. Drawing the match count first
             and correcting it read as a flicker — "(13)" then "(7 available)". */
          var $count = $('<span></span>');
          var matched = '(' + data.results_count + ')';
          resultsList.append(
            $('<div class="tt-view-all"></div>').append(
              $('<a></a>').attr('href', searchURL).text(seeAll + ' ').append($count)));

          /* Same rule as the results heading: the number worth showing is how
             many can be bought, not how many matched — Brigitte Granton is 166
             matches and 10 for sale.

             ⚠️ Fired ALONGSIDE the render, never before it. The panel draws
             immediately on the count it already has and the span is corrected
             when the second answer lands, so asking costs the type-ahead no
             latency. `token` drops a reply that arrives after the shopper has
             typed on, which is why the count cannot land on the wrong query.

             The link itself stays UNFILTERED. /search shows sold work with a
             badge (that is what Stage D settled), so the destination is the
             whole result set and the number names the buyable part of it —
             exactly the split the results heading makes. */
          var mine = ++countToken;
          var availURL = '/search?type=product&q=' + encodeURIComponent(resolvedQ + AVAIL_CLAUSE);
          $.get(availURL + '&view=json', function (body) {
            if (mine !== countToken) return;
            try {
              var n = (typeof body === 'string' ? JSON.parse(body) : body).results_count;
              $count.text(typeof n === 'number' ? '(' + n + ' available)' : matched);
            } catch (e) { $count.text(matched); }
          }).fail(function () {
            if (mine === countToken) $count.text(matched);
          });
        }

        resultsList.fadeIn(200);
      }

      /* `fallback`, when given, is the query to retry with if `q` matches
         nothing — which is how the phrase decision gets settled here.

         The submit path asks /search/suggest.json first because it has to
         choose before it navigates. The dropdown is already fetching results,
         so the same answer falls out of the response it wanted anyway: one
         request in the common case rather than two, and no 600ms probe bolted
         onto a type-ahead. A miss costs a second request and still beats the
         one-per-keystroke this replaced. */
      function fetchInto(q, fallback) {
        var searchURL = '/search?type=product&q=' + encodeURIComponent(q);

        if (inFlight) inFlight.abort();
        inFlight = $.getJSON(searchURL + '&view=json', function (data) {
          if (data.results_count == 0 && fallback) {
            fetchInto(fallback, null);
            return;
          }
          render(data, searchURL, q);
        });
      }

      function search() {
        var term = tidy(input.val());

        // Same guard the vendor used, at 3 characters instead of 4: don't
        // re-fetch a term that is already on screen.
        if (term.length < DROPDOWN_MIN_CHARS) return;
        if (term === input.attr('data-old-term')) return;
        input.attr('data-old-term', term);

        /* The same decision Enter makes, in the same order and out of the same
           two functions: dimensions win outright, then a multi-word phrase is
           quoted if it actually matches. Anything else searches as typed,
           which is what the vendor did for every query.

           ⚠️ No trailing wildcard, deliberately — see the note above. */
        var normalized = normalizeArtSearch(term);
        if (normalized !== term) {
          fetchInto(normalized, null);
          return;
        }
        if (!isPhraseCandidate(term)) {
          fetchInto(term, null);
          return;
        }
        fetchInto('"' + term + '"', term);
      }

      input.on('keyup change', function () {
        clearTimeout(timer);
        timer = setTimeout(search, DROPDOWN_DEBOUNCE_MS);
      });
    });
  }

  /* Registered now, at defer-execution time, rather than from the
     DOMContentLoaded path below — that is what puts this ready callback after
     the vendor's in the queue. */
  if (window.jQuery) {
    window.jQuery(function () { takeOverDropdown(window.jQuery); });
  }

  /* 4) The heading count, and the availability clause on the sort links.
   *
   * ONE request per search page settles both.
   *
   * THE COUNT. 71% of the catalogue is sold (1,374 of 1,943, measured
   * 2026-08-28), so a raw match count is mostly a count of work nobody can buy —
   * `sky` reports 991 where 269 are for sale. The available figure is a
   * different query, and Liquid can neither run a second search nor tally the
   * results it has (only 10 are exposed outside `paginate`), so it is asked for
   * here and written into the span sections/search.liquid marked. When the
   * query ALREADY carries the clause the server count is the available count,
   * so Liquid labels it and marks no probe — this then does nothing.
   *
   * THE SORT LINKS. search-sort.liquid writes `AND tag:available` into the
   * price-sort hrefs server-side, so they work with scripts off. But when
   * nothing matching is available the clause would land the visitor on an empty
   * page — `fog` is 4 results and 0 available. The same response answers that,
   * so a zero count swaps those hrefs back to the unfiltered ones the snippet
   * parked in data-cg-bare. Rewriting the href, never the link text: the label
   * still says what the sort does.
   *
   * Cost: 340ms median on the dev-server proxy (283-418, 12 samples), after
   * render and non-blocking. Every failure path leaves the server-rendered
   * count and the server-rendered hrefs exactly as they are.
   */
  function probeAvailable() {
    if (window.location.pathname.indexOf('/search') === -1) return;

    var span = document.querySelector('[data-cg-count][data-cg-probe]');
    var links = document.querySelectorAll('a[data-cg-avail][data-cg-bare]');

    /* sections/search.liquid hides the span until this answers, so every exit
       below has to show it again — with the server's match count if nothing
       better arrived. */
    function reveal() {
      if (span) span.style.visibility = '';
    }

    if (!window.fetch || !window.Promise) return reveal();
    if (!span && !links.length) return;

    var q = new URLSearchParams(window.location.search).get('q');
    if (!q) return reveal();
    /* Already narrowed — Liquid has labelled the count and the links are
       pointing where they should. Nothing to ask. */
    if (/tag:available/i.test(q)) return reveal();

    fetch('/search?type=product&view=json&q=' + encodeURIComponent(q + AVAIL_CLAUSE),
          { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (body) {
        /* ⚠️ Parsed from text, not r.json(): search.json.liquid is a Liquid
           template and Shopify serves it as text/html, so r.json() rejects on
           the content type even though the body is valid JSON. */
        var n = JSON.parse(body).results_count;
        if (typeof n !== 'number') return reveal();

        if (span) span.textContent = '(' + n + ' available)';
        reveal();

        if (n === 0) {
          for (var i = 0; i < links.length; i++) {
            links[i].setAttribute('href', links[i].getAttribute('data-cg-bare'));
            links[i].removeAttribute('data-cg-avail');
          }
        }
      })
      .catch(function () { reveal(); /* leave what the server rendered */ });
  }

  function init() {
    bindForms();
    /* Skip the probe when a redirect is already on its way — the replacement
       page runs this again with the corrected query. */
    if (redirectIfNeeded()) return;
    probeAvailable();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
